import { reactive } from 'vue';
import countryIdentity from './countryIdentity';

// Resolves the country whose company identity the page shows.
// Priority: order country -> localStorage -> browser region -> domain default.

const STORAGE_KEY = 'orderCountry';

// Countries each domain can serve; the first entry is that domain's default.
const DOMAIN_COUNTRIES = {
  '.com': ['GBR'],
  '.de': ['DEU', 'AUT'],
};

const FALLBACK_COUNTRY = 'GBR';

// navigator.language reports a 2-char region; the API uses 3-char ISO codes.
const REGION_TO_ISO3 = {
  AT: 'AUT',
  DE: 'DEU',
  GB: 'GBR',
};

function isSupported(code) {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(countryIdentity, code);
}

function fromStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // Some privacy modes throw on localStorage access.
    return null;
  }
}

function countriesForDomain() {
  const currentHost = window.location.hostname;
  const suffix = Object.keys(DOMAIN_COUNTRIES).find((key) => currentHost.endsWith(key));
  return suffix ? DOMAIN_COUNTRIES[suffix] : [FALLBACK_COUNTRY];
}

function fromBrowserRegion() {
  const browserLang = navigator.language || navigator.userLanguage;
  if (!browserLang) {
    return null;
  }
  const region = (browserLang.split('-')[1] || '').toUpperCase();
  const code = REGION_TO_ISO3[region];
  return code && countriesForDomain().includes(code) ? code : null;
}

function fromDomain() {
  return countriesForDomain()[0];
}

function resolveFallbackCountry() {
  return [fromStorage(), fromBrowserRegion(), fromDomain()].find(isSupported) || FALLBACK_COUNTRY;
}

export const countryState = reactive({
  code: resolveFallbackCountry(),
  resolved: true,
});

export function identityFor(code) {
  return countryIdentity[code] || countryIdentity[FALLBACK_COUNTRY];
}

export function awaitOrderCountry() {
  countryState.resolved = false;
}

export function setOrderCountry(code) {
  if (isSupported(code)) {
    countryState.code = code;
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      // Ignore write failures.
    }
  }
  countryState.resolved = true;
}
